// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import * as React from 'react';
import cx from 'classnames';
import { useParams } from 'react-router-dom';
import { toNumber } from 'lodash';
import { Table, Modal, Typography, Input, Button, Select, message } from 'antd';

import {
  IEmbeddedReportingItem,
  useDeleteEmbeddedReportingItemById,
  useGetEmbeddedReportingItems,
  useSaveEmbeddedReportingItem,
} from '@frontend/app/hooks';
import styles from './ClientEmbeddedReporting.scss';

const { Option } = Select;
const { Column } = Table;
const { Text } = Typography;
const { useState } = React;

interface IMatchParams {
  clientId: string;
}
interface IProps {
  className?: string;
}

const MAX_POSITION = 10;

/**
 * @type {React.FunctionComponent}
 */
const ClientEmbeddedReporting: React.FunctionComponent<React.PropsWithChildren<IProps>> = React.memo((props) => {
  const { clientId } = useParams<IMatchParams>();
  const context = {
    headers: {
      'requested-client-id': clientId,
    },
  };
  const {
    data: { embeddedReportingItems } = {},
    loading: loadingEmbeddedReportingItems,
    refetch,
    error: loadingItemsError,
  } = useGetEmbeddedReportingItems({ context });
  const [
    saveEmbeddedReportingItem,
    { loading: loadingSaveEmbeddedReportingItem, error: saveEmbeddedReportingItemError },
  ] = useSaveEmbeddedReportingItem({ context });
  const [deleteEmbeddedReportingItem, { loading: loadingDeleteEmbeddedReportingItem }] =
    useDeleteEmbeddedReportingItemById({ context });
  const [showAddOrEditModal, setShowAddOrEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IEmbeddedReportingItem | null>(null);
  const [itemInput, setItemInput] = useState<IEmbeddedReportingItem | null>(null);
  // New items default to Superset — Tableau embedded reporting is being
  // decommissioned (EPD-11450) and the backend rejects new Tableau items.
  // Derived once here so the selector, form fields, validation, and payload
  // all agree on which provider is actually in effect.
  const effectiveProvider = itemInput?.provider || (selectedItem ? 'TABLEAU' : 'SUPERSET');

  return (
    <div className={cx(styles.ClientEmbeddedReporting, props.className)}>
      <div className={styles.header}>
        <Text ellipsis>Embedded reporting pages (Tableau).</Text>
        <Button
          className={styles.addButton}
          type="primary"
          onClick={() => {
            setSelectedItem(null);
            setItemInput(null);
            setShowAddOrEditModal(true);
          }}
        >
          Add Embedded Reporting Item
        </Button>
      </div>
      {loadingItemsError && (
        <Text type="danger">
          Error while loading embedded reporting items:
          {loadingItemsError?.message}
        </Text>
      )}
      <Table
        rowKey={(record) => record.id}
        dataSource={embeddedReportingItems}
        loading={
          loadingEmbeddedReportingItems || loadingSaveEmbeddedReportingItem || loadingDeleteEmbeddedReportingItem
        }
      >
        <Column title="ID" dataIndex="id" />
        <Column title="Name" dataIndex="name" />
        <Column title="URL" dataIndex="url" />
        <Column title="Position" dataIndex="position" />
        <Column title="Created Date" dataIndex="createdDate" />
        <Column title="Updated Date" dataIndex="updatedDate" />
        <Column
          title="Edit"
          key="edit"
          render={(item: IEmbeddedReportingItem) => (
            <span>
              <Button
                type="primary"
                onClick={() => {
                  setSelectedItem(item);
                  // Clone object to avoid changing the original object
                  setItemInput({ ...item });
                  setShowAddOrEditModal(true);
                }}
              >
                Edit
              </Button>
            </span>
          )}
        />
        <Column
          title="Delete"
          key="delete"
          render={(item: IEmbeddedReportingItem) => (
            <span>
              <Button
                // Fresh! doesn't have a danger button, so we use antd version
                type="danger"
                onClick={async () => {
                  await deleteEmbeddedReportingItem({
                    variables: {
                      id: item.id,
                    },
                  });
                  refetch();
                }}
              >
                Delete
              </Button>
            </span>
          )}
        />
      </Table>
      <Modal
        className={styles.AddOrEditModal}
        title={selectedItem ? 'Edit Embedded Reporting Item' : 'Add Embedded Reporting Item'}
        visible={showAddOrEditModal}
        confirmLoading={loadingSaveEmbeddedReportingItem}
        onOk={async () => {
          // `required` on controlled antd inputs is a no-op, so validate explicitly.
          if (!itemInput?.name?.trim()) {
            message.error('Name is required');
            return;
          }
          if (effectiveProvider === 'SUPERSET' && !itemInput?.embedUuid?.trim()) {
            message.error('Embed UUID is required for a Superset report');
            return;
          }
          if (effectiveProvider !== 'SUPERSET' && !itemInput?.url?.trim()) {
            message.error('URL is required for a Tableau report');
            return;
          }
          await saveEmbeddedReportingItem({
            variables: {
              input: {
                name: itemInput.name,
                position: itemInput.position,
                provider: effectiveProvider,
                // Tableau items carry a URL; Superset items carry an embed UUID
                // plus optional extra client ids for multi-client aggregation.
                ...(effectiveProvider === 'SUPERSET'
                  ? {
                      embedUuid: itemInput?.embedUuid,
                      // Per-client reports are tenant-scoped (RLS = client + extras).
                      rlsScope: 'CLIENT',
                      addClientIds: (itemInput?.addClientIds || []).map((id) => id.trim()).filter(Boolean),
                    }
                  : { url: itemInput?.url }),
                ...(selectedItem && { id: selectedItem.id }),
              },
            },
          });
          setShowAddOrEditModal(false);
          refetch();
        }}
        onCancel={() => setShowAddOrEditModal(false)}
      >
        {saveEmbeddedReportingItemError && <Text type="danger">{saveEmbeddedReportingItemError.message}</Text>}
        {selectedItem && (
          <>
            <Text className={styles.inputLabel} ellipsis>
              ID
            </Text>
            <Input className={styles.input} placeholder="ID" value={selectedItem.id} disabled />
          </>
        )}
        <Text className={styles.inputLabel} ellipsis>
          Name
        </Text>
        <Input
          className={styles.input}
          placeholder="Name"
          value={itemInput?.name}
          onChange={(e) => setItemInput({ ...itemInput, name: e.target.value })}
          required
        />
        <Text className={styles.inputLabel} ellipsis>
          Provider
        </Text>
        <Select
          className={styles.select}
          value={effectiveProvider}
          onChange={(provider) => setItemInput({ ...itemInput, provider })}
        >
          {/*
            Tableau embedded reporting is being decommissioned (EPD-11450), so
            new items can no longer be created with provider=Tableau (also
            enforced server-side). Only show the option when editing an
            existing Tableau item, so it stays viewable/editable in place.
          */}
          {selectedItem?.provider === 'TABLEAU' && <Option value="TABLEAU">Tableau</Option>}
          <Option value="SUPERSET">Superset</Option>
        </Select>
        {effectiveProvider === 'SUPERSET' ? (
          <>
            <Text className={styles.inputLabel} ellipsis>
              Superset Embed UUID
            </Text>
            <Input
              className={styles.input}
              placeholder="Embed UUID (from Superset's Embed dashboard dialog)"
              value={itemInput?.embedUuid}
              onChange={(e) => setItemInput({ ...itemInput, embedUuid: e.target.value })}
              required
            />
            <Text className={styles.inputLabel} ellipsis>
              Additional Client IDs (optional — aggregate multiple clients in one dashboard)
            </Text>
            {(itemInput?.addClientIds || []).map((clientIdValue, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <Input
                  className={styles.input}
                  placeholder="client_id"
                  value={clientIdValue}
                  onChange={(e) => {
                    const next = [...(itemInput.addClientIds || [])];
                    next[index] = e.target.value;
                    setItemInput({ ...itemInput, addClientIds: next });
                  }}
                />
                <Button
                  onClick={() =>
                    setItemInput({
                      ...itemInput,
                      addClientIds: (itemInput.addClientIds || []).filter((_, i) => i !== index),
                    })
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            {/* Block wrapper so the button takes its own line and the next
                field ("Position") doesn't collide onto the same row. */}
            <div style={{ marginBottom: 10 }}>
              <Button
                onClick={() =>
                  setItemInput({ ...itemInput, addClientIds: [...(itemInput?.addClientIds || []), ''] })
                }
              >
                + Add client ID
              </Button>
            </div>
          </>
        ) : (
          <>
            <Text className={styles.inputLabel} ellipsis>
              URL
            </Text>
            <Input
              className={styles.input}
              placeholder="URL"
              value={itemInput?.url}
              onChange={(e) => setItemInput({ ...itemInput, url: e.target.value })}
              required
            />
          </>
        )}
        <Text className={styles.inputLabel} ellipsis>
          Position
        </Text>
        <Select
          className={styles.select}
          placeholder={itemInput?.position || 'Please select a position'}
          value={itemInput?.position}
          onChange={(position) => setItemInput({ ...itemInput, position: toNumber(position) })}
        >
          {Array.from({ length: MAX_POSITION }, (_, i) => (
            <Option key={i} value={i}>
              {i}
            </Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
});

export default ClientEmbeddedReporting;
